import { defineRouting, replaceLocale } from "@frend-digital/next/i18n";
import { defineNavigation } from "@frend-digital/next/i18n/navigation";
import { countryCodes } from "../../generated/countries";
import { removeTrailingSlash, addLeadingSlash } from "@frend-digital/utils";
import { getBaseUrl } from "@/lib/utils";
import { env } from "@/env.mjs";

const correctLanguage = (language: string) => {
	if (language === "dk") return "da";
	return language;
};

const languages = env.NEXT_PUBLIC_IS_STAGING ? ["en", "no"] : ["en", "no", "de", "sv", "fi", "da"];
export const localesWithCountries = languages.flatMap((locale) => {
	return countryCodes.map((country) => `${correctLanguage(locale)}-${country.country}`);
});

export const routing = defineRouting({
	languages,
	defaultLocale: "en-NO",
	locales: localesWithCountries,
	localePrefix: "always",
	/**
	 * Sets the preferred countries when multiple countries are available for a language
	 */
	preferredCountries: ["NO"],
	origin: "https://vikingfootwear.com",
	removeSegments: ["categories", "pages", "home"],
	groups: ["categories", "products", "pages"],
});

export const countries = countryCodes;

export const {
	Link: InitialLink,
	Form,
	permanentRedirect,
	redirect,
	usePathname,
	useRouter,
	useGeo,
	SearchParamLink,
	addLocale,
} = defineNavigation(routing);

export function findGeoByLanguage(
	language: string,
	preferredCountries: string[],
): string | undefined {
	const sortedCountries = countries.sort((a, b) => {
		const indexA = preferredCountries.indexOf(a.country);
		const indexB = preferredCountries.indexOf(b.country);

		// If a country is in the preferred list, it gets a higher priority
		if (indexA !== -1 && indexB !== -1) {
			return indexA - indexB; // Sort based on order in preferredCountries
		}
		if (indexA !== -1) {
			return -1; // a is preferred, so it comes first
		}
		if (indexB !== -1) {
			return 1; // b is preferred, so it comes first
		}
		return 0; // Neither is preferred, keep the original order
	});

	const country = sortedCountries.find((c) => c.language === language);
	if (!country) return undefined;
	return `${correctLanguage(language)}-${country?.country}`;
}

export function findLocaleByCountry(country: string): string | undefined {
	const match = countries.find((c) => c.country === country);
	if (!match) return undefined;

	const lang = correctLanguage(match.language);
	return `${lang}-${match.country}`;
}

export const canonicalLocales = countries.map(
	(country) => `${correctLanguage(country.language)}-${country.country.toUpperCase()}`,
);

export function findCanonicalLocale(language: string, country: string): string | undefined {
	const primaryMatch = canonicalLocales.find((c) => c === `${language}-${country}`);
	if (primaryMatch) return primaryMatch;

	// English matching
	if (language === "en") return `${language}-${country}`;

	const match = canonicalLocales.find((c) => c.startsWith(`${language}-`));

	return match;
}

export const sitemapLocales = Array.from(
	new Set([
		...countries.map((country) => `${correctLanguage(country.language)}-${country.country}`),
		...localesWithCountries.filter((c) => c.includes("en-")),
	]),
);

export const withDomain = (uri: string, locale?: string) => {
	return removeTrailingSlash(
		`${getBaseUrl()}${addLeadingSlash(
			replaceLocale(uri, locale || routing.defaultLocale, routing),
		)}`,
	);
};

export const removeSegments = (uri: string, segments: string[]) => {
	const split = uri.split("/");

	return split.filter((segment) => !segments.includes(segment)).join("/");
};

type StoryWithSlug = {
	translated_slugs?: Array<{ lang: string; path: string; [key: string]: unknown }> | null;
	full_slug: string;
	default_full_slug?: string | null;
	lang: string;
};

export function storySlugToSegments(story: StoryWithSlug, language: string): string[] {
	const translatedSlug = story.translated_slugs?.find((s) => s.lang === language);
	const lang = language === "en" ? "default" : language;
	const fallback = story.lang === lang ? story.full_slug : story.default_full_slug || story.full_slug;
	const rawSlug = translatedSlug?.path ?? fallback ?? "";
	const withoutLang = routing.languages.reduce(
		(s, l) => (s.startsWith(`${l}/`) ? s.slice(l.length + 1) : s),
		rawSlug,
	);
	return removeSegments(withoutLang, routing.removeSegments).split("/").filter(Boolean);
}
